import { MatDialog } from '@angular/material/dialog';
import { Injectable, Optional } from '@angular/core';
import { ErrorDialogComponent } from 'app/shared/dialogs/error-dialog/error-dialog.component';
import { catchError, lastValueFrom, Observable } from 'rxjs';
import { ApiService } from 'app/shared/apis/api.service';
import { snakeCase } from 'lodash';
import { IFilterPagination } from 'app/shared/interfaces/filter-interface';
import { IPagination } from 'app/shared/interfaces/pagination.dto';

@Injectable({
    providedIn: 'root',
})
export class BaseService<T> {
    constructor(public api: ApiService, public _matDialog: MatDialog, @Optional() public endpoint?: string, @Optional() public name_id: string = null) {}

    isCreating(item: T): boolean {
        return !item[this.getIdName];
    }

    // Proxy handler para adicionar .promise a métodos que retornam Observable
    withPromise<U>(observable: Observable<U>): Observable<U> & { promise: Promise<U> } {
        const obs = observable as Observable<U> & { promise: Promise<U> };
        Object.defineProperty(obs, 'promise', {
            get: () => lastValueFrom(observable),
            enumerable: false,
            configurable: true,
        });
        return obs;
    }

    list(filter?: IFilterPagination<T>) {
        const $request = this.api.getSub<IPagination<T>>(`${this.endpoint}/list`, filter);
        return this.withPromise($request);
    }

    create(item: Partial<T>) {
        const $request = this.api.postSub<T>(`${this.endpoint}/create`, item).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Cadastrar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    getById(id: string | number) {
        const $request = this.api.getSub<T>(`${this.endpoint}/${id}`).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Buscar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    update(item: Partial<T>) {
        const id = item[this.getIdName];
        const $request = this.api.putSub<T>(`${this.endpoint}/${id}`, item).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Atualizar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    delete(item: T) {
        const id = item[this.getIdName];
        const $request = this.api.deleteSub<void>(`${this.endpoint}/${id}`).pipe(
            catchError((error) => {
                this.openDialogErro({
                    title: `Erro ao Deletar ${this.endpoint}!!`,
                    message: this.getErrorMessage(error),
                });
                throw error;
            }),
        );
        return this.withPromise($request);
    }

    openDialogErro(item: { title: string; message: string }): void {
        this._matDialog.open(ErrorDialogComponent, {
            panelClass: 'notification-dialog',
            width: '500px',
            data: item,
        });
    }

    getErrorMessage(error: any): string {
        return (
            error.error?.errors?.map((e: any) => e.descricao).join('<br />') ||
            error.error.message ||
            error.error.sqlMessage ||
            error.error.descricao ||
            'Erro desconhecido'
        );
    }

    get getIdName(): string {
        return this.name_id || `${snakeCase(this.endpoint)}_id`;
    }
}
